import { parseISO } from "date-fns";
import Head from "expo-router/head";
import React, { useMemo, useRef, useState } from "react";
import { Platform, StyleSheet, Text, View } from "react-native";

import { useTranslation } from "@luna-restaurant/i18n";
import type { SchoolGuestOrder } from "@luna-restaurant/school-api";
import {
  PageContainer,
  ThemedHeadlineText,
  ThemedLoader,
  ThemedTable,
  type ThemedTableColumn,
  type ThemedTableHeaderColumn,
  ThemedTitle,
  useCustomTheme,
} from "@luna-restaurant/ui";

import { ContactBanner } from "@/components/ContactBanner";
import { GroupAddOutlineIcon } from "@/components/GroupAddOutlineIcon.web";
import { SecondaryButton } from "@/components/SecondaryButton";
import { StatusBannerHost } from "@/components/StatusBannerHost";
import {
  NewGuestOrderForm,
  type NewGuestOrderFormValues,
} from "@/components/forms/NewGuestOrderForm";
import {
  CancelGuestOrderModal,
  type CancelGuestOrderModalOrder,
} from "@/components/modals/CancelGuestOrderModal";
import { EditGuestOrderModal } from "@/components/modals/EditGuestOrderModal";
import { useClosingTimes } from "@/hooks/useClosingTimes";
import { useCollection } from "@/hooks/useCollection";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isApiError, useRequest } from "@/hooks/useRequest";
import { useScrollIntoView } from "@/hooks/useScrollIntoView";
import { useStatusBanner } from "@/hooks/useStatusBanner";
import { formatBerlinDateOnly } from "@/utils/closingTimes/berlinDateOnly";
import { getStatusBannerErrorMessage } from "@/utils/getStatusBannerErrorMessage";
import { isOrderableDate } from "@/utils/guestOrderDeadline";

type GuestOrderStatus = "active" | "cancelled";

export type GuestOrder = {
  id: string;
  totalMeals: string;
  editor: string;
  date: string;
  comment?: string;
  positions: { id: number; menuCode: string; quantity: number }[];
  status: GuestOrderStatus;
};

export default function GuestOrdersScreen() {
  const { fonts, colors } = useCustomTheme();
  const { t, i18n } = useTranslation();
  const { currentUser } = useCurrentUser();
  const request = useRequest();
  const formSectionRef = useRef<View | null>(null);
  const overviewHeadlineRef = useRef<View | null>(null);
  const handleScrollToForm = useScrollIntoView(formSectionRef);
  const handleScrollToOverviewHeadline = useScrollIntoView(overviewHeadlineRef);
  const { showStatusBanner } = useStatusBanner();
  const { items: closingTimes } = useClosingTimes({
    type: "PUBLIC_HOLIDAY",
    fetchAllPages: true,
  });
  const guestOrdersQuery = useCollection<SchoolGuestOrder>({
    queryKey: ["guest-orders", currentUser?.school],
    path: currentUser?.school ? `${currentUser.school}/guest-orders` : "",
    enabled: !!currentUser?.school,
    fetchAllPages: true,
    query: { "order[servingAt]": "asc" },
  });
  const [locallyCancelledOrderIds, setLocallyCancelledOrderIds] = useState<
    string[]
  >([]);
  const [orderToCancel, setOrderToCancel] = useState<GuestOrder | null>(null);
  const [cancelValidationError, setCancelValidationError] = useState<
    string | null
  >(null);
  const [isCancellingGuestOrder, setIsCancellingGuestOrder] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<GuestOrder | null>(null);

  const locallyCancelledOrderIdSet = useMemo(
    () => new Set(locallyCancelledOrderIds),
    [locallyCancelledOrderIds],
  );

  const guestOrders = useMemo<GuestOrder[]>(() => {
    return guestOrdersQuery.items.map((order) => {
      const orderId = String(order.id);
      const isCancelled =
        Boolean(order.cancelledAt) || locallyCancelledOrderIdSet.has(orderId);

      return {
        id: orderId,
        totalMeals: String(
          order.positions.reduce((sum, pos) => sum + pos.quantity, 0),
        ),
        editor: order.editedBy ?? "--",
        date: order.servingAt,
        comment: order.comment ?? undefined,
        positions: order.positions.map((p) => ({
          id: p.id,
          menuCode: p.menuCode,
          quantity: p.quantity,
        })),
        status: isCancelled ? "cancelled" : "active",
      };
    });
  }, [guestOrdersQuery.items, locallyCancelledOrderIdSet]);
  const isLoading = guestOrdersQuery.isLoading;
  const cancelledLabelStyle = useMemo(
    () => ({
      ...fonts.bold,
      ...styles.cancelledLabel,
      color: colors.muted,
      borderColor: colors.muted,
    }),
    [fonts.bold, colors.muted],
  );
  const getGuestOrderRow = (order: GuestOrder): ThemedTableColumn[] => [
    {
      type: "text",
      label: formatDate(order.date),
      style: fonts.bold,
    },
    {
      type: "text",
      label: order.totalMeals,
    },
    { type: "text", label: order.editor },
    { type: "text", label: order.comment ?? "--" },
  ];

  const formatDate = (value: string) =>
    formatBerlinDateOnly(value, i18n.language) ?? value;

  const handleGuestOrderSubmitSuccess = (
    values: NewGuestOrderFormValues,
    info: { skippedDates: string[] },
  ) => {
    const baseMessage = t("guestOrders.form.successBanner.message", {
      dateFrom: formatDate(values.dateFrom),
      dateTo: formatDate(values.dateTo),
    });
    const message =
      info.skippedDates.length > 0
        ? `${baseMessage}\n\n${t("guestOrders.form.someDatesSkipped", {
            dates: info.skippedDates.join(", "),
          })}`
        : baseMessage;
    showStatusBanner({
      type: "success",
      message,
      onViewPress: handleScrollToOverviewHeadline,
      viewButtonLabel: t("common.form.successBanner.viewLabel"),
      dismissible: true,
      dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
      autoDismissAfterSeconds: 20,
    });
    handleScrollToForm();
  };

  const handleGuestOrderSubmitError = (error: unknown) => {
    showStatusBanner({
      type: "error",
      message: getStatusBannerErrorMessage(error, t),
      dismissible: true,
      dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
      autoDismissAfterSeconds: 20,
    });
    handleScrollToForm();
  };

  const header = (
    <>
      <ThemedTitle
        subtitle={t("guestOrders.subtitle")}
        title={t("guestOrders.title")}
        customIcon={<GroupAddOutlineIcon />}
      />
      <StatusBannerHost />
    </>
  );

  const headerColumns = useMemo<ThemedTableHeaderColumn[]>(
    () => [
      {
        label: t("guestOrders.table.header.date"),
        width: 21,
      },
      {
        width: 37.5,
        parts: [
          {
            label: t("guestOrders.table.header.totalMeals"),
            style: fonts.regular,
          },
        ],
        style: fonts.bold,
      },
      {
        label: t("guestOrders.table.header.nameOfCreator"),
        width: 37.5,
      },
      {
        label: t("guestOrders.table.header.comment"),
        width: 80,
      },
      {
        label: t("guestOrders.table.header.actions"),
        width: 24,
      },
    ],
    [fonts, t],
  );

  const getOrderAvailability = (order: GuestOrder) => {
    const now = new Date();
    const parsedDate = parseISO(order.date);
    const hasValidDate = !Number.isNaN(parsedDate.getTime());

    const canModify =
      order.status !== "cancelled" &&
      hasValidDate &&
      isOrderableDate(parsedDate, now, closingTimes);

    return { canModify };
  };

  const actionColumn = (order: GuestOrder) => {
    if (order.status === "cancelled") {
      return {
        type: "text" as const,
        label: t("guestOrders.table.row.actions.cancelled"),
        style: cancelledLabelStyle,
      };
    }

    const { canModify } = getOrderAvailability(order);

    const actions: {
      label: string;
      icon: "pencil" | "close-circle";
      onPress: () => void;
    }[] = [];

    if (canModify) {
      actions.push({
        label: t("guestOrders.table.row.actions.edit"),
        icon: "pencil",
        onPress: () => {
          setOrderToEdit(order);
        },
      });

      actions.push({
        label: t("guestOrders.table.row.actions.cancel"),
        icon: "close-circle",
        onPress: () => {
          setCancelValidationError(null);
          setOrderToCancel(order);
        },
      });
    }

    if (actions.length === 0) {
      return {
        type: "text" as const,
        label: "",
      };
    }

    return {
      type: "action" as const,
      actions,
    };
  };

  const handleCancelConfirm = async (
    modalOrder: CancelGuestOrderModalOrder,
  ) => {
    if (!currentUser?.school) {
      console.error("Cannot cancel guest order without a school");
      return;
    }

    if (
      !isOrderableDate(parseISO(modalOrder.dateRaw), new Date(), closingTimes)
    ) {
      setCancelValidationError(
        t("guestOrders.cancelValidation.deadlinePassed"),
      );
      return;
    }

    setCancelValidationError(null);
    setIsCancellingGuestOrder(true);

    const markOrderCancelled = () => {
      setLocallyCancelledOrderIds((previousIds) =>
        previousIds.includes(modalOrder.id)
          ? previousIds
          : [...previousIds, modalOrder.id],
      );
      setOrderToCancel(null);
    };

    let isCancelled = false;

    try {
      await request<void>(
        `${currentUser.school}/guest-orders/${modalOrder.id}/cancel`,
        {
          method: "POST",
        },
      );

      markOrderCancelled();
      isCancelled = true;
    } catch (error) {
      const alreadyCancelled = isApiError(error) && error.status === 409;
      console.error("Failed to cancel guest order:", error);
      showStatusBanner({
        type: "error",
        message: alreadyCancelled
          ? t("guestOrders.cancelValidation.alreadyCancelled")
          : getStatusBannerErrorMessage(error, t),
        dismissible: true,
        dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
        autoDismissAfterSeconds: 20,
      });
      setOrderToCancel(null);
      if (alreadyCancelled) {
        markOrderCancelled();
        isCancelled = true;
      }
    } finally {
      setIsCancellingGuestOrder(false);
    }

    if (isCancelled) {
      // The cancellation itself already succeeded and is reflected locally, so
      // a failing refresh must not be reported as a failed cancellation.
      try {
        await guestOrdersQuery.refetch();
      } catch (error) {
        console.error(
          "Failed to refresh guest orders after cancellation:",
          error,
        );
      }
    }
  };

  const modalOrder: CancelGuestOrderModalOrder | null = orderToCancel
    ? {
        id: orderToCancel.id,
        dateRaw: orderToCancel.date,
        date: formatDate(orderToCancel.date),
        totalMeals: orderToCancel.totalMeals,
        comment: orderToCancel.comment,
      }
    : null;

  return (
    <View>
      <Head>
        <title>{t("titles.guestOrders")}</title>
      </Head>
      <PageContainer header={header} style={styles.pageContainer}>
        <View ref={formSectionRef} style={styles.sectionGuestOrderForm}>
          <ThemedHeadlineText
            type={t("guestOrders.type.new")}
            headline={t("guestOrders.createGuestOrder")}
            textContent={t("guestOrders.createGuestOrderText")}
          />
          <NewGuestOrderForm
            onSubmitSuccess={handleGuestOrderSubmitSuccess}
            onSubmitError={handleGuestOrderSubmitError}
          />
        </View>

        <View style={styles.sectionGuestOrderOverview}>
          <View ref={overviewHeadlineRef}>
            <ThemedHeadlineText
              type={t("guestOrders.type.current")}
              headline={t("guestOrders.guestOrdersOverview")}
              textContent={t("guestOrders.guestOrdersOverviewText")}
            />
          </View>

          {isLoading ? (
            <View style={styles.listStateContainer}>
              <ThemedLoader spinnerSize={50} />
            </View>
          ) : guestOrders.length === 0 ? (
            <View style={styles.noGuestOrdersContainer}>
              <Text style={[styles.noGuestOrdersText, fonts.regular]}>
                {t("guestOrders.noGuestOrders.text")}
              </Text>
              <SecondaryButton
                label={t("guestOrders.newGuestOrderButtonText")}
                iconName="plus"
                iconPosition="left"
                onPress={handleScrollToForm}
              />
            </View>
          ) : (
            <View style={styles.tableContainer}>
              <ThemedTable headerColumns={headerColumns}>
                {guestOrders.map((order) => (
                  <ThemedTable.Row
                    key={order.id}
                    columns={[...getGuestOrderRow(order), actionColumn(order)]}
                  />
                ))}
              </ThemedTable>
            </View>
          )}
        </View>
      </PageContainer>
      <EditGuestOrderModal
        visible={Boolean(orderToEdit)}
        guestOrder={orderToEdit}
        onSubmitSuccess={() => {
          showStatusBanner({
            type: "success",
            message: t("guestOrders.editModal.successMessage"),
            onViewPress: handleScrollToOverviewHeadline,
            viewButtonLabel: t("common.form.successBanner.viewLabel"),
            dismissible: true,
            dismissButtonLabel: t("common.form.successBanner.dismissLabel"),
            autoDismissAfterSeconds: 20,
          });
        }}
        onSubmitError={handleGuestOrderSubmitError}
        onDismiss={() => setOrderToEdit(null)}
      />
      <CancelGuestOrderModal
        visible={Boolean(orderToCancel)}
        order={modalOrder}
        isSubmitting={isCancellingGuestOrder}
        validationError={cancelValidationError}
        onDismiss={() => {
          if (!isCancellingGuestOrder) {
            setCancelValidationError(null);
            setOrderToCancel(null);
          }
        }}
        onConfirm={(order) => {
          void handleCancelConfirm(order);
        }}
      />
      <ContactBanner />
    </View>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    gap: 120,
    paddingTop: 32,
  },
  sectionGuestOrderForm: {
    paddingTop: 32,
    gap: 64,
  },
  sectionGuestOrderOverview: {
    gap: 64,
  },
  listStateContainer: {
    minHeight: 300,
  },
  noGuestOrdersContainer: {
    paddingVertical: 32,
    alignItems: "center",
    gap: 32,
  },
  noGuestOrdersText: {
    fontSize: 32,
    lineHeight: 40,
    textAlign: "center",
  },
  tableContainer: {
    marginBottom: 32,
  },
  cancelledLabel: {
    borderRadius: 4,
    borderWidth: 1,
    paddingHorizontal: 8,
    fontSize: 16,
    lineHeight: 20.8,
    alignSelf: Platform.OS === "web" ? "center" : "flex-start",
    marginRight: Platform.OS === "web" ? 0 : 16,
  },
});
